import { Link, useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useId, useState, type FormEvent } from 'react'
import type { Attachment, AttachmentKind, ItemWithPath, Loan, Movement, Photo } from '@ousse/shared'
import { api } from '../lib/api'
import { attachmentUrl, formatFileSize } from '../lib/attachments'
import { useHousehold } from '../lib/household'
import { photoThumbUrl, photoUrl } from '../lib/photos'
import { WhereIsIt } from '../components/WhereIsIt'
import { Alert, ConfirmButton, ErrorAlert, Field } from '../components/ui'

/** Fiche objet : photos, « Où c'est ? », infos complémentaires, documents joints. */
export function ItemDetailPage() {
  const { t } = useTranslation()
  const { itemId } = useParams<{ itemId: string }>()
  const { current } = useHousehold()
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const fileId = useId()
  const attachId = useId()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const canWrite = current && current.role !== 'viewer' && !current.readOnly

  const item = useQuery({
    queryKey: ['item', current?.id, itemId],
    enabled: Boolean(current?.id && itemId),
    queryFn: () => api.get<ItemWithPath>(`/households/${current!.id}/items/${itemId}`),
  })

  if (!current || !itemId) return <p role="status">{t('common.loading')}</p>
  if (item.isLoading) return <p role="status">{t('common.loading')}</p>
  if (item.isError) return <ErrorAlert error={item.error} />
  const data = item.data!
  const label = data.name?.trim() || t('inventory.untitledItem')
  const photos = data.photos ?? []
  const attachments = data.attachments ?? []
  const preview = previewId ? attachments.find((a) => a.id === previewId) : null

  return (
    <>
      <h1>{label}</h1>
      <ErrorAlert error={error} />

      {photos.length > 0 ? (
        <ul className="photo-strip">
          {photos.map((p) => (
            <li key={p.id}>
              <a href={photoUrl(current.id, p.id)} target="_blank" rel="noreferrer">
                <img src={photoThumbUrl(current.id, p.id)} alt={label} className="thumb" />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t('inventory.noPhotos')}</p>
      )}

      <WhereIsIt item={data} householdId={current.id} />

      {canWrite && (
        <div className="field">
          <label htmlFor={fileId}>{t('inventory.addPhoto')}</label>
          <input
            id={fileId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            onChange={async (e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (!file) return
              setError(null)
              setBusy(true)
              try {
                const form = new FormData()
                form.append('file', file, file.name || 'photo.jpg')
                await api.postForm<Photo>(`/households/${current.id}/items/${data.id}/photos`, form)
                await qc.invalidateQueries({ queryKey: ['item', current.id, itemId] })
                await qc.invalidateQueries({ queryKey: ['contents'] })
              } catch (err) {
                setError(err)
              } finally {
                setBusy(false)
              }
            }}
          />
        </div>
      )}


      <ComplementaryInfo
        key={data.updatedAt}
        item={data}
        householdId={current.id}
        canWrite={Boolean(canWrite)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['item', current.id, itemId] })}
        onError={setError}
      />

      <section className="card stack" aria-labelledby="attachments-heading">
        <h2 id="attachments-heading">{t('inventory.attachmentsTitle')}</h2>
        <p className="muted">{t('inventory.attachmentsHint')}</p>
        {attachments.length === 0 ? (
          <p className="muted">{t('inventory.noAttachments')}</p>
        ) : (
          <ul className="list">
            {attachments.map((a) => (
              <li key={a.id}>
                <div className="stack" style={{ flex: 1 }}>
                  <strong>{a.fileName}</strong>
                  <span className="muted">
                    {t(`inventory.attachmentKind.${a.kind}`)} · {formatFileSize(a.sizeBytes, t)}
                  </span>
                </div>
                <button type="button" className="btn secondary" onClick={() => setPreviewId(a.id)}>
                  {t('inventory.openAttachment')}
                </button>
                {canWrite && (
                  <ConfirmButton
                    message={t('inventory.deleteAttachmentConfirm', { name: a.fileName })}
                    onConfirm={async () => {
                      setError(null)
                      try {
                        await api.delete(`/households/${current.id}/attachments/${a.id}`)
                        if (previewId === a.id) setPreviewId(null)
                        await qc.invalidateQueries({ queryKey: ['item', current.id, itemId] })
                      } catch (e) {
                        setError(e)
                      }
                    }}
                  >
                    {t('inventory.deleteAttachment')}
                  </ConfirmButton>
                )}
              </li>
            ))}
          </ul>
        )}

        {canWrite && (
          <AttachmentUpload
            inputId={attachId}
            householdId={current.id}
            itemId={data.id}
            busy={busy}
            setBusy={setBusy}
            onError={setError}
            onUploaded={() => void qc.invalidateQueries({ queryKey: ['item', current.id, itemId] })}
          />
        )}

        {preview && (
          <div className="stack">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3>{preview.fileName}</h3>
              <button type="button" className="btn ghost" onClick={() => setPreviewId(null)}>
                {t('common.close')}
              </button>
            </div>
            {preview.mimeType === 'application/pdf' ? (
              <iframe
                title={preview.fileName}
                src={attachmentUrl(current.id, preview.id)}
                style={{ width: '100%', minHeight: '70vh', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
              />
            ) : (
              <a href={attachmentUrl(current.id, preview.id)} target="_blank" rel="noreferrer">
                <img
                  src={attachmentUrl(current.id, preview.id)}
                  alt={preview.fileName}
                  style={{ maxWidth: '100%', borderRadius: 'var(--radius)' }}
                />
              </a>
            )}
          </div>
        )}
      </section>

      <LoanSection
        householdId={current.id}
        itemId={data.id}
        itemName={label}
        canWrite={Boolean(canWrite)}
        onError={setError}
      />

      <MovementsSection householdId={current.id} itemId={data.id} />

      <div className="row">
        {data.locationId ? (
          <Link className="btn secondary" to={`/storage?locationId=${data.locationId}`}>
            {t('inventory.openLocation')}
          </Link>
        ) : (
          <Link className="btn secondary" to="/storage?uncategorized=1">
            {t('inventory.uncategorized')}
          </Link>
        )}
        {canWrite && (
          <ConfirmButton
            message={t('inventory.deleteItemConfirm', { name: label })}
            onConfirm={async () => {
              setError(null)
              try {
                await api.delete(`/households/${current.id}/items/${data.id}`)
                await qc.invalidateQueries()
                void navigate('/storage')
              } catch (e) {
                setError(e)
              }
            }}
          >
            {t('inventory.deleteItem')}
          </ConfirmButton>
        )}
      </div>
    </>
  )
}

function formatWhen(iso: string, locale: string, dateOnly = false) {
  return new Intl.DateTimeFormat(locale, dateOnly ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  )
}

function LoanSection({
  householdId,
  itemId,
  itemName,
  canWrite,
  onError,
}: {
  householdId: string
  itemId: string
  itemName: string
  canWrite: boolean
  onError: (e: unknown) => void
}) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const [borrower, setBorrower] = useState('')
  const [dueAt, setDueAt] = useState('')
  const loans = useQuery({
    queryKey: ['loans', householdId, itemId],
    queryFn: () =>
      api.get<{ items: Loan[]; active: Loan | null }>(`/households/${householdId}/items/${itemId}/loans`),
  })

  const lend = useMutation({
    mutationFn: () =>
      api.post<Loan>(`/households/${householdId}/items/${itemId}/loans`, {
        borrower: borrower.trim(),
        dueAt: dueAt || null,
      }),
    onSuccess: async () => {
      setBorrower('')
      setDueAt('')
      await qc.invalidateQueries({ queryKey: ['loans', householdId, itemId] })
      await qc.invalidateQueries({ queryKey: ['activity'] })
    },
    onError,
  })

  const markReturned = useMutation({
    mutationFn: (loanId: string) => api.post<Loan>(`/households/${householdId}/loans/${loanId}/return`, {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['loans', householdId, itemId] })
      await qc.invalidateQueries({ queryKey: ['activity'] })
    },
    onError,
  })

  if (loans.isLoading) return null
  if (loans.isError) return <ErrorAlert error={loans.error} />
  const active = loans.data!.active
  const history = loans.data!.items

  return (
    <section className="card stack" aria-labelledby="loan-heading">
      <h2 id="loan-heading">{t('loan.title')}</h2>
      {active ? (
        <div className="stack">
          <Alert kind="warning">
            <strong>{t('loan.active', { borrower: active.borrower })}</strong>
            <br />
            <span className="muted">{t('loan.lentAt', { date: formatWhen(active.lentAt, i18n.language) })}</span>
            {active.dueAt && (
              <>
                <br />
                <span className="muted">{t('loan.due', { date: formatWhen(`${active.dueAt}T12:00:00.000Z`, i18n.language, true) })}</span>
              </>
            )}
          </Alert>
          {canWrite && (
            <ConfirmButton
              message={t('loan.returnConfirm', { name: itemName })}
              onConfirm={() => {
                onError(null)
                markReturned.mutate(active.id)
              }}
            >
              {t('loan.return')}
            </ConfirmButton>
          )}
        </div>
      ) : canWrite ? (
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            onError(null)
            lend.mutate()
          }}
        >
          <p className="muted">{t('loan.noneActive')}</p>
          <Field
            label={t('loan.borrower')}
            value={borrower}
            onChange={(e) => setBorrower(e.target.value)}
            required
            maxLength={120}
            placeholder={t('loan.borrowerPlaceholder')}
          />
          <Field label={t('loan.dueAt')} type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          <button className="btn secondary" disabled={lend.isPending || !borrower.trim()}>
            {t('loan.lend')}
          </button>
        </form>
      ) : (
        <p className="muted">{t('loan.noneActive')}</p>
      )}

      <details>
        <summary style={{ cursor: 'pointer', fontWeight: 600, minHeight: 48, alignItems: 'center', display: 'flex' }}>
          {t('loan.history')}
        </summary>
        {history.length === 0 ? (
          <p className="muted">{t('loan.empty')}</p>
        ) : (
          <ul className="list">
            {history.map((loan) => (
              <li key={loan.id}>
                <div className="stack" style={{ flex: 1 }}>
                  <strong>{loan.borrower}</strong>
                  <span className="muted">{t('loan.lentAt', { date: formatWhen(loan.lentAt, i18n.language) })}</span>
                  {loan.returnedAt && (
                    <span className="muted">
                      {t('loan.returnedAt', { date: formatWhen(loan.returnedAt, i18n.language) })}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </details>
    </section>
  )
}

function MovementsSection({ householdId, itemId }: { householdId: string; itemId: string }) {
  const { t, i18n } = useTranslation()
  const movements = useQuery({
    queryKey: ['movements', householdId, itemId],
    queryFn: () => api.get<{ items: Movement[] }>(`/households/${householdId}/items/${itemId}/movements`),
  })

  if (movements.isLoading) return null
  if (movements.isError) return <ErrorAlert error={movements.error} />
  const items = movements.data!.items

  return (
    <section className="card stack" aria-labelledby="movements-heading">
      <h2 id="movements-heading">{t('movements.title')}</h2>
      {items.length === 0 ? (
        <p className="muted">{t('movements.empty')}</p>
      ) : (
        <ul className="list">
          {items.map((m) => {
            const from = m.fromLocationName?.trim() || t('movements.uncategorized')
            const to = m.toLocationName?.trim() || t('movements.uncategorized')
            return (
              <li key={m.id}>
                <div className="stack" style={{ flex: 1 }}>
                  <strong>{t('movements.fromTo', { from, to })}</strong>
                  <span className="muted">
                    {formatWhen(m.movedAt, i18n.language)}
                    {m.displayName ? ` · ${t('movements.by', { name: m.displayName })}` : ''}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function ComplementaryInfo({
  item,
  householdId,
  canWrite,
  onSaved,
  onError,
}: {
  item: ItemWithPath
  householdId: string
  canWrite: boolean
  onSaved: () => void
  onError: (e: unknown) => void
}) {
  const { t, i18n } = useTranslation()
  const [brand, setBrand] = useState(item.brand ?? '')
  const [model, setModel] = useState(item.model ?? '')
  const [serialNumber, setSerialNumber] = useState(item.serialNumber ?? '')
  const [purchasePlace, setPurchasePlace] = useState(item.purchasePlace ?? '')
  const [purchaseDate, setPurchaseDate] = useState(item.purchaseDate ?? '')
  const [warrantyEnd, setWarrantyEnd] = useState(item.warrantyEnd ?? '')
  const [price, setPrice] = useState(
    item.purchasePriceCents != null ? String(item.purchasePriceCents / 100) : '',
  )

  const save = useMutation({
    mutationFn: () => {
      let cents: number | null = null
      if (price.trim() !== '') {
        const n = Number(price.replace(',', '.'))
        if (Number.isNaN(n) || n < 0) throw Object.assign(new Error('validation_error'), { code: 'validation_error' })
        cents = Math.round(n * 100)
      }
      return api.patch(`/households/${householdId}/items/${item.id}`, {
        brand: brand.trim() || null,
        model: model.trim() || null,
        serialNumber: serialNumber.trim() || null,
        purchasePlace: purchasePlace.trim() || null,
        purchaseDate: purchaseDate || null,
        warrantyEnd: warrantyEnd || null,
        purchasePriceCents: cents,
        purchaseCurrency: cents != null ? 'EUR' : null,
      })
    },
    onSuccess: () => onSaved(),
    onError: (e) => onError(e),
  })

  const hasAny =
    Boolean(item.brand || item.model || item.serialNumber || item.purchasePlace || item.purchaseDate || item.warrantyEnd || item.purchasePriceCents != null)

  return (
    <details className="card" open={hasAny && !canWrite}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, minHeight: 48, alignItems: 'center', display: 'flex' }}>
        {t('inventory.extraTitle')}
      </summary>
      <p className="muted">{t('inventory.extraHint')}</p>
      {canWrite ? (
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            onError(null)
            try {
              save.mutate()
            } catch {
              onError({ code: 'validation_error' })
            }
          }}
        >
          <Field label={t('inventory.brand')} value={brand} onChange={(e) => setBrand(e.target.value)} maxLength={120} />
          <Field label={t('inventory.model')} value={model} onChange={(e) => setModel(e.target.value)} maxLength={120} />
          <Field
            label={t('inventory.serialNumber')}
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            maxLength={120}
          />
          <Field
            label={t('inventory.purchasePrice')}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            hint={t('inventory.purchasePriceHint')}
          />
          <Field
            label={t('inventory.purchaseDate')}
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
          />
          <Field
            label={t('inventory.purchasePlace')}
            value={purchasePlace}
            onChange={(e) => setPurchasePlace(e.target.value)}
            maxLength={200}
          />
          <Field
            label={t('inventory.warrantyEnd')}
            type="date"
            value={warrantyEnd}
            onChange={(e) => setWarrantyEnd(e.target.value)}
          />
          <button type="submit" className="btn" disabled={save.isPending}>
            {t('common.save')}
          </button>
          {save.isSuccess && <Alert kind="success">{t('inventory.extraSaved')}</Alert>}
        </form>
      ) : (
        <dl className="stack">
          {item.brand && (
            <>
              <dt className="muted">{t('inventory.brand')}</dt>
              <dd>{item.brand}</dd>
            </>
          )}
          {item.model && (
            <>
              <dt className="muted">{t('inventory.model')}</dt>
              <dd>{item.model}</dd>
            </>
          )}
          {item.serialNumber && (
            <>
              <dt className="muted">{t('inventory.serialNumber')}</dt>
              <dd>{item.serialNumber}</dd>
            </>
          )}
          {item.purchasePriceCents != null && (
            <>
              <dt className="muted">{t('inventory.purchasePrice')}</dt>
              <dd>
                {(item.purchasePriceCents / 100).toLocaleString(i18n.language, {
                  style: 'currency',
                  currency: item.purchaseCurrency || 'EUR',
                })}
              </dd>
            </>
          )}
          {item.purchaseDate && (
            <>
              <dt className="muted">{t('inventory.purchaseDate')}</dt>
              <dd>{item.purchaseDate}</dd>
            </>
          )}
          {item.purchasePlace && (
            <>
              <dt className="muted">{t('inventory.purchasePlace')}</dt>
              <dd>{item.purchasePlace}</dd>
            </>
          )}
          {item.warrantyEnd && (
            <>
              <dt className="muted">{t('inventory.warrantyEnd')}</dt>
              <dd>{item.warrantyEnd}</dd>
            </>
          )}
          {!hasAny && <p className="muted">{t('inventory.extraEmpty')}</p>}
        </dl>
      )}
    </details>
  )
}

function AttachmentUpload({
  inputId,
  householdId,
  itemId,
  busy,
  setBusy,
  onError,
  onUploaded,
}: {
  inputId: string
  householdId: string
  itemId: string
  busy: boolean
  setBusy: (v: boolean) => void
  onError: (e: unknown) => void
  onUploaded: () => void
}) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<AttachmentKind>('other')

  return (
    <div className="stack">
      <label className="stack">
        <span>{t('inventory.attachmentKindLabel')}</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as AttachmentKind)} style={{ minHeight: 48 }}>
          <option value="invoice">{t('inventory.attachmentKind.invoice')}</option>
          <option value="manual">{t('inventory.attachmentKind.manual')}</option>
          <option value="warranty">{t('inventory.attachmentKind.warranty')}</option>
          <option value="other">{t('inventory.attachmentKind.other')}</option>
        </select>
      </label>
      <div className="field">
        <label htmlFor={inputId}>{t('inventory.addAttachment')}</label>
        <input
          id={inputId}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={async (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            onError(null)
            setBusy(true)
            try {
              const form = new FormData()
              form.append('file', file, file.name || 'document')
              form.append('kind', kind)
              await api.postForm<Attachment>(`/households/${householdId}/items/${itemId}/attachments`, form)
              onUploaded()
            } catch (err) {
              onError(err)
            } finally {
              setBusy(false)
            }
          }}
        />
      </div>
    </div>
  )
}
